// SwarmHistory — recorded Slip Analysis sessions, click-to-expand (read-only).
//
// Fix: history must REVEAL the recorded analysis inline (like the Extractor's
// drop-down), not re-enter the analyse flow. Each row expands to show every
// leg's recorded swarm verdict — the Moderator pick / consensus + confidence +
// risk — fetched lazily on open. No re-analysis; this is a record viewer.
//
// (Sessions can read 'confirming' even when analysed — the status isn't flipped
// — so we key "analysed" off the recorded per-leg verdict, not the status.)

import { useCallback, useState } from "react";

import { getBrowserClient } from "../../supabase/browser.ts";
import type { SwarmHistoryRow } from "./useSwarmHistory.ts";

interface RecordedLeg {
  id: string;
  leg_index: number;
  home_team: string | null;
  away_team: string | null;
  slip_market: string | null;
  slip_selection: string | null;
  slip_odds: number | null;
  consensus_prediction: string | null;
  avg_confidence: number | null;
  moderator_value: { prediction?: string | null; selection?: string | null; confidence?: number | null } | null;
  risk_flag: string | null;
}

type LegState = RecordedLeg[] | "loading" | "error";

function fmtDate(s: string): string {
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return ""; }
}

export function SwarmHistory({ rows }: { rows: SwarmHistoryRow[] }): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  const [legsBy, setLegsBy] = useState<Record<string, LegState>>({});

  const toggle = useCallback(async (sid: string) => {
    if (openId === sid) { setOpenId(null); return; }
    setOpenId(sid);
    if (legsBy[sid]) return; // cached
    setLegsBy((p) => ({ ...p, [sid]: "loading" }));
    try {
      const { data } = await getBrowserClient()
        .from("swarm_legs" as never)
        .select("id,leg_index,home_team,away_team,slip_market,slip_selection,slip_odds,consensus_prediction,avg_confidence,moderator_value,risk_flag")
        .eq("session_id" as never, sid as never)
        .order("leg_index" as never);
      setLegsBy((p) => ({ ...p, [sid]: ((data ?? []) as unknown as RecordedLeg[]) }));
    } catch {
      setLegsBy((p) => ({ ...p, [sid]: "error" }));
    }
  }, [openId, legsBy]);

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontFamily: "var(--swarm-font-mono, monospace)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.6, marginBottom: 10 }}>
        Recent analyses
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.slice(0, 12).map((h) => {
          const open = openId === h.id;
          const legs = legsBy[h.id];
          return (
            <div key={h.id} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, background: "rgba(255,255,255,0.02)", overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => void toggle(h.id)}
                data-testid="swarm-history-row"
                aria-expanded={open}
                style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "10px 14px", background: "transparent", border: "none", color: "inherit", fontSize: 13, cursor: "pointer", textAlign: "left" }}
              >
                <span style={{ opacity: 0.5, width: 12 }}>{open ? "▾" : "▸"}</span>
                <span style={{ fontWeight: 600, minWidth: 60 }}>{h.leg_count ?? "?"} legs</span>
                <span style={{ opacity: 0.8 }}>{typeof h.combined_odds === "number" ? `@ ${h.combined_odds.toFixed(2)}` : ""}</span>
                <span style={{ opacity: 0.55, fontSize: 12 }}>{h.source_hint === "extractor_handoff" ? "from Extractor" : (h.bookmaker ?? "")}</span>
                <span style={{ marginLeft: "auto", opacity: 0.5, fontSize: 12 }}>{fmtDate(h.created_at)}</span>
              </button>
              {open && (
                <div style={{ padding: "4px 14px 12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  {legs === "loading" && <div style={{ opacity: 0.6, fontSize: 12, padding: "8px 0" }}>Loading recorded analysis…</div>}
                  {legs === "error" && <div style={{ opacity: 0.6, fontSize: 12, padding: "8px 0" }}>Couldn&rsquo;t load this analysis.</div>}
                  {Array.isArray(legs) && legs.length === 0 && <div style={{ opacity: 0.6, fontSize: 12, padding: "8px 0" }}>No analysed legs recorded for this session.</div>}
                  {Array.isArray(legs) && legs.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 8 }}>
                      {legs.map((l) => {
                        const swarmPick = l.moderator_value?.prediction ?? l.consensus_prediction;
                        const conf = typeof l.moderator_value?.confidence === "number" ? l.moderator_value.confidence : l.avg_confidence;
                        return (
                          <div key={l.id} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600 }}>{l.home_team} v {l.away_team}</div>
                            <div style={{ opacity: 0.75 }}>
                              Your pick: {l.slip_market ?? "?"} {l.slip_selection ?? ""}{typeof l.slip_odds === "number" ? ` @ ${l.slip_odds}` : ""}
                            </div>
                            <div style={{ opacity: 0.9 }}>
                              {swarmPick
                                ? (<>Swarm: <strong>{swarmPick}</strong>{typeof conf === "number" ? ` · ${Math.round(conf)}% confidence` : ""}{l.risk_flag && l.risk_flag !== "none" ? ` · ${String(l.risk_flag).replace(/_/g, " ")}` : ""}</>)
                                : (<span style={{ opacity: 0.55 }}>Not analysed</span>)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
